<template>
    <div id="page-associator">
        <div class="display-6 m-2">Page Manager</div>
        <form id="page-update-form" class="needs-validation" novalidate>
            <div class="row m-3">
                <div class="col-md-9">
                    <label for="company" class="m-1 form-label">Company</label>
                    <select v-model="company" class="form-select" id="company" @change="clearPage()" required>
                        <option v-for="[key, value] in companyOptions" :key="value" :value=value :disabled="value === '0'">
                            {{ key }}
                        </option>
                    </select>
                </div>
            </div>
            <div v-if="isCompanySelected">
                <div v-if="!isPageSelected">
                    <div v-if="isPagesLoading">
                        <div class="row m-3 mt-5">
                            <div class="col-md-9 text-center">
                                <span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>
                                <span>Loading...</span>
                            </div>
                        </div>
                    </div>
                    <div v-else>
                        <div v-if="isDisplayImportPage">
                            <div class="row m-3">
                                <div class="col-md-5">
                                    <label class="m-1" for="importPageId">Page ID</label>
                                    <input type="text"  class="form-control" v-model="importPageRequest.PageID" id="importPageId" placeholder="Page ID" required />
                                </div>
                            </div>
                            <div class="row m-3">
                                <div class="col-md-5">
                                    <label class="m-1 form-label" for="pageOwnerType">Page Owner</label>
                                    <select v-model="importPageRequest.OwnerType" class="form-select" id="pageOwnerType">
                                        <option v-for="[key, value] in pageOwnerOptions" :key="value" :value="value">
                                            {{ key }}
                                        </option>
                                    </select>
                                </div>
                            </div>
                            <div class="row m-3">
                                <div class="col-md-9">
                                    <button :disabled=isImporting class="btn btn-primary" @click="importPage">
                                        <span v-if="isImporting" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                        Import
                                    </button>
                                    <button :disabled=isImporting class="btn btn-secondary" @click="cancelImport">Cancel</button>
                                </div>
                            </div>
                            <div v-if="displayImportError">
                                <div class="row m-4">
                                    <div class="col-md-9 alert alert-danger">
                                        <p class="display-1 text-danger text-center"><font-awesome-icon :icon="['far', 'xmark']"/></p>
                                        <h3 class="text-center text-danger m-2">Failure</h3>
                                        <p class="text-center m-2">{{ importResponseMessage }}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div v-else>
                            <div class="row m-3">
                                <div class="col-md-6">
                                    <div class="form-group">
                                        <label class="m-1" for="pageSearch">Search Existing Managed Pages:</label>
                                        <div class="row">
                                            <div class="col-md-9">
                                                <input id="pageSearch" class="form-control" type="text" v-model="pageSearchInput" placeholder="Search by page name, domain, or ID" />
                                            </div>
                                            <div class="col-md-3">
                                                <select class="form-select" v-model="statusFilter">
                                                    <option selected value="">All</option>
                                                    <option value="0">Active</option>
                                                    <option value="1">Inactive</option>
                                                </select> 
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-1 mt-4">
                                    <p class="mt-2"> - or - </p>
                                </div>
                                <div class="col-md-3">
                                    <button class="btn btn-secondary mt-4" @click="displayImportPage">Import New</button>
                                </div>
                            </div>  
                            <div class="row m-2 mt-0" v-if="isSearchedForPage">
                                <div class="col-md-9 overflow-auto" style="max-height: 520px; cursor: pointer;">
                                    <ul class="list-group" v-for="(page, index) in filteredPages" :key="page.PageID">
                                        <li class="list-group-item" :class="getPageClass(index)" style="cursor: pointer">
                                            <div class="d-flex w-100 justify-content-between" @click="pageClick(page)">
                                                <h6 class="mb-1">{{ page.PageName }} {{ page.OwnerType === 1 ? " - Client Owned" : ""}}                                                                                                                                                                                                                                                 
                                                    <span v-if=page.HasWebhook class="text-success">                                                                                                                                                                                                                                                                                    
                                                        <font-awesome-icon icon="check" class="mt-3"></font-awesome-icon>                                                                                                                                                                                                                                                               
                                                        Webhook                                                                                                                                                                                                                                                                                                                         
                                                    </span>
                                                </h6>  
                                                <div v-if="page.Company === 1"  class="text-end" @click.stop>
                                                    <button
                                                        class="btn btn-sm"
                                                        :class="page.HasWebhook ? 'btn-outline-secondary' : 'btn-outline-success'"
                                                        :disabled="togglingWebhookPageId === page.PageID"
                                                        @click="toggleWebhook(page)">
                                                        <span v-if="togglingWebhookPageId === page.PageID" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                                        {{ page.HasWebhook ? 'Webhook Disable' : 'Webhook Enable' }}
                                                    </button>
                                                </div>
                                            </div>
                                            <small class="mb-1">{{ page.PageID }} - {{ page.DomainName }} - <span :class="page.Status === 0 ? 'text-success' : 'text-danger'">{{ page.Status === 0 ? "ACTIVE" : "INACTIVE" }}</span></small>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div v-else>
                    <div class="row m-3">
                        <div class="col-md-9">
                            <div class="list-group">
                                <li class="list-group-item">
                                    <div class="d-flex w-100 justify-content-between">
                                        <h6 class="mb-1">{{ selectedPageName }}</h6>
                                        <span @click="clearPage()" class="badge bg-secondary rounded-pill" style="cursor: pointer;">
                                            <font-awesome-icon icon="xmark" style="margin: 2px; margin-right: 2px;"></font-awesome-icon>
                                        </span>
                                    </div>
                                    <small>{{ updateRequest.PageID }}</small>
                                </li>
                            </div>
                        </div>
                    </div>
                    <div>
                        <div v-if="!displayDomainSearch" class="row m-3">
                            <div class="col-md-9">
                                <label class="m-1" for="domain">Selected Domain</label>
                                <div class="input-group">
                                    <input type="text" disabled v-model="updateRequest.DomainName" id="domain" class="form-control" placeholder="Domain">
                                    <div class="input-group-append">
                                        <button class="btn btn-outline-secondary" type="button" @click="changeDomainClick">Change</button>
                                    </div>
                                </div>
                                <small class="form-text text-muted small">Site ID: {{ updateRequest.DDID }}</small>
                            </div>
                        </div>
                        <div class="row m-3" v-if="displayDomainSearch">
                            <div class="row">
                                <div class="col-md-8">
                                    <label class="" for="domainSearch">Search for domains</label>
                                    <div class="input-group">
                                        <input type="text" v-model="domainSearchInput" class="form-control" id="domainSearch" placeholder="Search for a domain" />
                                        <div class="input-group-append">
                                            <button class="btn btn-primary" @click="searchPlatforms">Search</button>
                                        </div>
                                    </div>
                                </div>
                                <div class="col-md-1 mt-4">
                                    <button class="btn btn-secondary" @click="cancelSearchPlatforms">Cancel</button>
                                </div>
                            </div>
                            <div class="row">
                                <div class="col-md-9 m-3 overflow-auto" style="max-height: 520px; cursor: pointer;">
                                    <ul class="list-group" v-for="(witcoDomain, index) in platformSearchResults" :key="witcoDomain.Id">
                                        <li class="list-group-item" :class="getPageClass(index)" @click="domainClick(witcoDomain)" style="cursor: pointer">
                                            <div class="d-flex w-100 justify-content-between">
                                                <h6 class="mb-1">{{ witcoDomain.DomainName }}</h6>
                                            </div>
                                            <small class="mb-1 small">{{ witcoDomain.Status === 0 ? "ACTIVE" : "INACTIVE" }}</small>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div v-if="displayPageProperties">
                        <div v-if="isWitcoOwned">
                            <div class="row m-3">
                                <div class="col-md-9">
                                    <label class="m-1" for="about">About/Bio</label>
                                    <textarea style="height: 150px;" v-model="updateRequest.About" class="form-control" id="about" placeholder="About/Bio" required />
                                </div>
                            </div>
                            <div class="row m-3">
                                <div class="col-md-9">
                                    <label class="m-1" for="Phone">Phone</label>
                                    <input type="text" v-model="updateRequest.Phone" class="form-control" id="phone" placeholder="Phone Number" required />
                                    <small class="form-text text-muted small">Should start with the country code and only be numerical characters</small>
                                </div>
                            </div>
                            <div class="row m-3">
                                <div class="col-md-9">
                                    <label class="m-1" for="website">Website URL</label>
                                    <input type="text" v-model="updateRequest.Website" class="form-control" id="website" placeholder="https://www.site-domain.com" required />
                                    <small class="form-text text-muted small">Should start with https:// or http://</small>
                                </div>
                            </div>
                        </div>
                        <div class="row m-3">
                            <div class="col-md-9">
                                <button :disabled=disableUpdate  class="btn btn-primary mt-3" @click="updateManagedPage">
                                    <span v-if="isUpdating" class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                                    Update
                                </button>
                                <button :disabled=disableUpdate class="btn btn-secondary mt-3" @click="clearPage()">Cancel</button>
                            </div>
                        </div>
                    </div>
                    
                </div>
                <div v-if="displaySuccess">
                    <div class="row m-4">
                        <div class="col-md-9 alert alert-success">
                            <p class="display-1 text-success text-center"><font-awesome-icon :icon="['far', 'check-circle']"/></p>
                            <h3 class="text-center text-success m-2">Success!</h3>
                            <p class="text-center m-2">The page has been updated in Facebook and in our CINC system</p>
                        </div>
                    </div>
                </div>
                <div v-if=displayPartialSuccess>
                    <div class="row m-4">
                        <div class="col-md-9 alert alert-warning">
                            <p class="display-1 text-warning text-center"><font-awesome-icon :icon="['far', 'check-circle']"/></p>
                            <h3 class="text-center text-warning m-2">Partial Success</h3>
                            <p class="text-center m-2">{{ updateResponseMessage }}</p>
                        </div>
                    </div>
                </div>
                <div v-if="displayError">
                    <div class="row m-4">
                        <div class="col-md-9 alert alert-danger">
                            <p class="display-1 text-danger text-center"><font-awesome-icon :icon="['far', 'xmark']"/></p>
                            <h3 class="text-center text-danger m-2">Failure</h3>
                            <p class="text-center m-2">{{ updateResponseMessage }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </form>
    </div>
</template>

<style></style>

<script lang="ts">
import { Component, Vue } from 'vue-property-decorator';
import { ManagedPage } from '../../../models/ManagedPage';
import { WitcoPlatform } from '../../../models/WitcoPlatform'
import { UpdatePageRequest } from '../../../models/request/UpdatePageRequest';
import { ImportPageRequest } from '../../../models/request/ImportPageRequest';
import { Company } from '../../../enums/Company';
import { PageOwnerType } from '../../../enums/PageOwnerType';
import { UpdatePageWebhookRequest } from '../../../models/request/UpdatePageWebhookRequest';

@Component
export default class PageManager extends Vue {
    
    form: any;
    displaySuccess: boolean = false;
    displayPartialSuccess: boolean = false;
    displayError: boolean = false;
    displayImportError: boolean = false;
    displayDomainSearch: boolean = false;
    isDisplayImportPage: boolean = false;
    isPagesLoading : boolean = false;
    isUpdating: boolean = false;
    isImporting: boolean = false;
    disableUpdate: boolean = false;

    company: Company = 0;
    importResponseMessage: string = "";
    updateResponseMessage: string = "";
    selectedPageName: string = "";
    pageSearchInput: string = "";
    domainSearchInput : string = "";
    statusFilter: string = "";
    togglingWebhookPageId: string = "";

    platformSearchResults: WitcoPlatform[] = new Array<WitcoPlatform>();
    managedPages: ManagedPage[] = new Array<ManagedPage>();
    updateRequest: UpdatePageRequest = new UpdatePageRequest();
    importPageRequest: ImportPageRequest = new ImportPageRequest();

    get companyOptions() {
    return Object.entries(Company)
      .filter(([key, value]) => typeof value === 'number')
      .map(([key, value]) => { if(value === 0) { key = "Please Select a Company";} return [key, value] });
    }

    get pageOwnerOptions() {
        return Object.entries(PageOwnerType)
        .filter(([key, value]) => typeof value === 'number')
        .map(([key,value]) => [key, value.toString()]);
    }

    get isCompanySelected() : boolean {
        return this.company !== 0;
    }

    get filteredPages() : ManagedPage[] {
        if(this.statusFilter === "" ) {
            if(this.pageSearchInput === "") {
                return this.managedPages;
            }

            return this.managedPages.filter((page : ManagedPage) => (
                (page.PageName.toLowerCase().includes(this.pageSearchInput.toLowerCase()) || page.PageID.includes(this.pageSearchInput) || page.DomainName.includes(this.pageSearchInput))
                && page.Company === this.company
            ));
        }
        else{
            if(this.statusFilter === "0") {
                return this.managedPages.filter((page : ManagedPage) => (
                    (page.PageName.toLowerCase().includes(this.pageSearchInput.toLowerCase()) || page.PageID.includes(this.pageSearchInput) || page.DomainName.includes(this.pageSearchInput))
                    && page.Company === this.company
                    && page.Status === 0
                ));
            }
            else {
                return this.managedPages.filter((page : ManagedPage) => (
                    (page.PageName.toLowerCase().includes(this.pageSearchInput.toLowerCase()) || page.PageID.includes(this.pageSearchInput) || page.DomainName.includes(this.pageSearchInput))
                    && page.Company === this.company
                    && page.Status !== 0
                ));
            }
        }
    }

    get isSearchedForPage() : boolean {
        return this.pageSearchInput.length > 0;
    }

    get isPageSelected() : boolean {
        return this.updateRequest.PageID !== "";
    }

    get displayPageProperties() : boolean {
        return !this.displayDomainSearch
    }

    get isWitcoOwned() : boolean {
        return this.updateRequest.OwnerType === PageOwnerType.Witco;
    }

    getPageClass(index: number) {
        return index % 2 == 0 ? '':'list-group-item-light';
    }

    async loadManagedPages() {
        this.isPagesLoading = true;
        this.managedPages = await this.$store.dispatch('managedPage/getAllManagedPages');
        this.isPagesLoading = false;
    }

    async mounted() {
        this.loadManagedPages();
    }

    async pageClick(page: ManagedPage) {

        this.selectedPageName = page.PageName;
        this.updateRequest.PageID = page.PageID;
        this.updateRequest.DDID = page.DDID === undefined || page.DDID === null ? "" : page.DDID;
        this.updateRequest.DomainName = page.DomainName;
        this.updateRequest.About = page.About;
        this.updateRequest.Phone = page.Phone;
        this.updateRequest.Website = page.Website;
        this.updateRequest.OwnerType = page.OwnerType;
                
        if((page.DomainName === undefined || page.DomainName === null || page.DomainName === "") && !(page.DDID === "" || page.DDID === null || page.DDID === undefined)) {
            let witcoPlatform :WitcoPlatform = new WitcoPlatform();
            if(this.company === Company.CINC)
                witcoPlatform = await this.$store.dispatch('cincPlatform/getPlatform', page.DDID);
            if(this.company === Company.RealGeeks)
                witcoPlatform = await this.$store.dispatch('realGeeksPlatform/getPlatform', page.DDID);

            if(witcoPlatform !== null && witcoPlatform !== undefined) {
                this.updateRequest.DomainName = witcoPlatform.DomainName;
                if(page.Website === undefined || page.Website === null || page.Website === "") {
                    this.updateRequest.Website = `https://www.${witcoPlatform.DomainName}`;
                }
            }
        }
    }

    async domainClick(witcoPlatform: WitcoPlatform) {
        this.updateRequest.DomainName = witcoPlatform.DomainName;
        this.updateRequest.DDID = witcoPlatform.Id;
        if(!witcoPlatform.DomainName.startsWith("www."))
            this.updateRequest.Website = `https://www.${witcoPlatform.DomainName}`;
        else
            this.updateRequest.Website = `https://${witcoPlatform.DomainName}`;

        let countryCode = (witcoPlatform.Country === "US" || witcoPlatform.Country === "CA") ? "1" : witcoPlatform.Country === "MX" ? "52" : "";
        if(!(witcoPlatform.ContactPhone === null || witcoPlatform.ContactPhone === undefined))
            this.updateRequest.Phone =  `${countryCode}${witcoPlatform.ContactPhone.replace(/\D/g,'')}`;

        if(this.company === Company.CINC)
            this.updateRequest.About = `${witcoPlatform.CompanyName}\r\n${witcoPlatform.ContactFirstName} ${witcoPlatform.ContactLastName}\r\n${witcoPlatform.ContactAddress}\r\n${witcoPlatform.ContactCity}, ${witcoPlatform.ContactState} ${witcoPlatform.ContactZip}\r\n${witcoPlatform.ContactPhone}`;
        
        this.displayDomainSearch = false;
        this.domainSearchInput = ``;
        this.platformSearchResults = new Array();
    }

    async changeDomainClick() {
        this.displayDomainSearch = true;
    }

    clearPage() {
        this.updateRequest = new UpdatePageRequest();
        this.displaySuccess = false;
        this.displayPartialSuccess = false;
        this.displayError = false;
        this.pageSearchInput = "";
        this.displayDomainSearch = false;
        if(this.form)
            this.form.classList.remove('was-validated');
    }

    displayImportPage(e: Event) {
        e.preventDefault();
        e.stopPropagation();
        this.isDisplayImportPage = true;
    }

    async importPage(e :Event) {
        e.preventDefault();
        e.stopPropagation();
        this.displayImportError = false;
        this.isImporting = true;
        this.importPageRequest.Company = this.company;
        await this.$store.dispatch('managedPage/importManagedPage', this.importPageRequest)
            .then(async (response: any) => {
                await this.pageClick(<ManagedPage>response);
                this.isDisplayImportPage = false;        
            })
            .catch((error: any) => {
                this.displayImportError = true;
                this.importResponseMessage = error;
            })
            .finally(() => {
                this.isImporting = false;
                this.domainSearchInput = "";
            });
    }

    async searchPlatforms(e: Event) {
        e.preventDefault();
        e.stopPropagation();
        if(this.company === Company.CINC)
            this.platformSearchResults = await this.$store.dispatch('cincPlatform/getPlatforms', this.domainSearchInput);

        if(this.company === Company.RealGeeks)
            this.platformSearchResults = await this.$store.dispatch('realGeeksPlatform/getPlatforms', this.domainSearchInput);
    }

    async cancelSearchPlatforms(e: Event) {
        e.preventDefault();
        e.stopPropagation();
        this.domainSearchInput = '';
        this.platformSearchResults = new Array();
        this.displayDomainSearch = false;
    }

    async cancelImport(e: Event) {
        e.preventDefault();
        e.stopPropagation();
        this.isDisplayImportPage = false;
        this.displayImportError = false;
        this.importResponseMessage = '';
        this.importPageRequest.PageID = '';
        this.importPageRequest.OwnerType = PageOwnerType.Witco;
    }

    async toggleWebhook(page: ManagedPage) {
        this.togglingWebhookPageId = page.PageID;
        const updateRequest : UpdatePageWebhookRequest = {
            pageId : page.PageID,
            hasWebhook : page.HasWebhook,
            company : page.Company
        }
        await this.$store.dispatch('managedPage/toggleWebhook', updateRequest)
            .catch((error: any) => {
                this.displayError = true;
                this.updateResponseMessage = `An error occurred while toggling the webhook. ${error}`;
            })
            .finally(() => {
                this.togglingWebhookPageId = "";
            });
    }

    async updateManagedPage(e: Event) {
        this.displayError = false;
        this.displaySuccess = false;
        this.displayPartialSuccess = false;
        this.form = document.getElementsByClassName('needs-validation')[0];
        this.form.classList.add('was-validated');
        if(this.form){
            e.preventDefault();
            e.stopPropagation();
            if (this.form.checkValidity() === true) { 
                this.disableUpdate = true;
                
                this.updateRequest.Company = this.company;

                this.isUpdating = true;
                await this.$store.dispatch('managedPage/updateManagedPage', this.updateRequest)
                .then((response) => {
                    
                    if(response.status === 200) {
                        this.displaySuccess = true;
                        this.displayPartialSuccess = false;
                        this.$data.updateResponseMessage = "";
                    }
                    if(response.status === 206) {
                        this.displayPartialSuccess = true;
                        this.displaySuccess = false;
                        this.updateResponseMessage = response.data;
                    }
                })
                .catch((error) => {
                    this.displayError = true;
                    this.updateResponseMessage = `An error occurred while updating the page. ${error}`;
                }).finally(() => {
                    this.disableUpdate = false;
                    this.isUpdating = false;
                }); 
            }
        }
    }
}
</script>